import { Eye } from "lucide-react";
import { SolidButton } from "../global/SolidButton";
import { UseForm } from "./UseForm";
import React, { useState } from "react";
import { Button } from "@mui/material";

export const RedirectForm = ({
  isEditMode,
  handleUpdate,
  handleAdd,
  onCancel, // function called when user cancels the form/modal
  initialValues: initialValuesProp = {},
  exceptions = {}, // { fieldName: true } to skip validation for a field
  customValidators = {}, // { fieldName: (value) => string|false|null }
}) => {
  const defaultValues = {
    title: "",
    referenceCode: "",
    redirectLink: "",
    dateTime: "",
    isActive: 1,
  };

  const validate = (values) => {
    const errors = {};

    const shouldSkip = (field) => !!exceptions && !!exceptions[field];
    const runCustom = (field, value) => {
      const fn = customValidators && customValidators[field];
      if (typeof fn === "function") {
        try {
          const res = fn(value);
          // if validator returns a string treat it as the error message
          if (typeof res === "string" && res) return res;
          // if returns false treat as generic invalid
          if (res === false) return "Invalid value";
        } catch (e) {
          return "Invalid value";
        }
      }
      return null;
    };

    if (!shouldSkip("title")) {
      const custom = runCustom("title", values.title);
      if (custom) {
        errors.title = custom;
      } else if (!values.title || !values.title.trim()) {
        errors.title = "Title is required";
      }
    }

    if (!shouldSkip("referenceCode")) {
      const custom = runCustom("referenceCode", values.referenceCode);
      if (custom) {
        errors.referenceCode = custom;
      } else if (!values.referenceCode || Number(values.referenceCode) <= 0) {
        errors.referenceCode = "Reference code is required";
      }
    }

    if (!shouldSkip("redirectLink")) {
      const custom = runCustom("redirectLink", values.redirectLink);
      if (custom) {
        errors.redirectLink = custom;
      } else if (!values.redirectLink || !values.redirectLink.trim()) {
        errors.redirectLink = "Link is required";
      }
    }

    return errors;
  };

  const onSubmit = async (values) => {
    try {
      console.log("Form Values:", values);
      if (isEditMode) {
        if (typeof handleUpdate === "function") await handleUpdate(values);
      } else {
        if (typeof handleAdd === "function") await handleAdd(values);
      }
      toast.success("Submitted successfully");
    } catch (error) {
      toast.error("Submission failed");
      throw error;
    }
  };
  // Merge defaults with any provided initialValues prop (used for edit)
  const startingValues = { ...defaultValues, ...(initialValuesProp || {}) };

  const { values, errors, isSubmitting, handleChange, handleSubmit, reset } =
    UseForm(startingValues, validate);

  // If parent provides new initial values (e.g., entering edit mode), reset the form
  React.useEffect(() => {
    if (initialValuesProp && Object.keys(initialValuesProp).length) {
      reset({ ...defaultValues, ...initialValuesProp });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValuesProp]);

  const type = [
    { id: 1, label: "Manual Entry" },
    { id: 2, label: "Import Configuration" },
  ];
  const handleTabClick = (id) => {
    setActiveTab(id);
  };
  const [activeTab, setActiveTab] = useState(1);
  const isManual = activeTab == 1;
  const [jsonInput, setJsonInput] = useState("");
  const [jsonError, setJsonError] = useState(null);

  const isValidJSON = (value) => {
    setJsonInput(value);

    if (!value.trim) {
      setJsonError(null); // no error for empty field

      return;
    }

    try {
      JSON.parse(value);
      setJsonError(null); //Json valid
    } catch (error) {
      setJsonError("Invalid JSON Format");
    }
  };

  return (
    <>
      <div className="space-y-2 w-full mt-4 ">
        {/* tabs */}
        {!isEditMode && (
          <div
            className=" py-2 p-2 w-1/2 bg-slate-300/25 dark:bg-gray-700/30 backdrop-blur-xl rounded-lg   
              "
          >
            {type.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={` text-xs font-medium border 
                 mr-2
           p-1 rounded-sm transition-all duration-200 transform
           text-slate-800 dark:text-slate-300 font-normal ${
             tab.id === activeTab
               ? "bg-white dark:bg-slate-100/2 border dark:via-slate-800 dark:to-slate-900 border-slate-300"
               : "border-transparent cursor-pointer"
           }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {isManual ? (
          <>
            {/* title */}
            <h1 className="text-sm font-medium dark:text-slate-200 mt-4">
              Title*
            </h1>
            <input
              type="text"
              name="title"
              value={values.title}
              onChange={handleChange}
              placeholder="e.g.,Telegram Link"
              className={`w-full px-2 py-2 bg-slate-100 dark:bg-slate-800 rounded-md  text-slate-800 dark:text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all border ${
                errors.title
                  ? "border-red-400"
                  : "border-slate-200 dark:border-slate-700"
              }`}
            />
            {errors.title && (
              <p className="text-xs text-red-500 font-normal">{errors.title}</p>
            )}
            {/* Reference Code* */}
            <h1 className="text-sm font-medium dark:text-slate-200 mt-4">
              Reference Code*
            </h1>
            <input
              type="text"
              name="referenceCode"
              value={values.referenceCode}
              onChange={handleChange}
              placeholder="e.g.,telegram,facebook_ads"
              className={`w-full px-2 py-2 bg-slate-100 dark:bg-slate-800 rounded-md  text-slate-800 dark:text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all border ${
                errors.referenceCode
                  ? "border-red-400"
                  : "border-slate-200 dark:border-slate-700"
              }`}
            />
            {errors.referenceCode && (
              <p className="text-xs text-red-500 font-normal ">
                {errors.referenceCode}
              </p>
            )}
            <p className="text-[10px] text-gray-500 dark:text-slate-400 font-semibold ">
              This will appear as ?ref=
              {values.referenceCode ? values.referenceCode : "your-ref"}
            </p>
            {/* Redirect Link*/}
            <h1 className="text-sm font-medium dark:text-slate-200 mt-4">
              Rediect Link*
            </h1>
            <textarea
              name="redirectLink"
              value={values.redirectLink}
              onChange={handleChange}
              placeholder="e.g.,https:example.com?ref={ref}"
              className={`w-full h-20 p-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-sm text-slate-800 placeholder-slate-500 dark:text-white resize-none mt-2 border ${
                errors.redirectLink
                  ? "border-red-400"
                  : "border-slate-300 dark:border-slate-700"
              }`}
            />
            {errors.redirectLink && (
              <p className="text-xs text-red-500 font-normal mb-2">
                {errors.redirectLink}
              </p>
            )}
            {/* action buttons */}
            <div className="flex items-center justify-end space-x-4 mt-4">
              <button
                className="px-4 py-2 rounded-lg bg-slate-400/20 dark:bg-slate-800 text-slate-800 dark:text-slate-300 text-sm font-semibold hover:bg-slate-400/30 transition-all"
                type="button"
                onClick={() => {
                  if (typeof onCancel === "function") onCancel();
                }}
              >
                Cancel
              </button>

              <SolidButton
                onClick={() => handleSubmit(onSubmit)}
                disabled={isSubmitting}
                children={<span>{isEditMode ? "Update" : "Add Redirect"}</span>}
              />
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col">
              {/* Import Configuration */}
              <span className="text-md font-bold text-slate-900 dark:text-slate-100 mt-4">
                Configuration JSON
              </span>
              <span className="text-[12px] font-normal text-slate-700 dark:text-slate-100">
                Paste your redirect configuration JSON to quickly setup a new
                redirect
              </span>
              <textarea
                name="jsonInput"
                value={jsonInput}
                onChange={(e) => isValidJSON(e.target.value)}
                placeholder="Paste your redirect configuration JSON here"
                className={`w-full h-60 p-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-xs text-slate-800 placeholder-slate-500 dark:text-white resize-none mt-2 border ${"border-slate-300 dark:border-slate-700"}`}
              />
              {jsonError && (
                <p className="text-xs text-red-500 font-normal my-2">
                  {jsonError}
                </p>
              )}

              {jsonError && (<div className="flex items-center justify-between mt-2">
                <span className="text-[14px] text-red-500 font-semibold my-2">
                    "Invalid Json Configuration"
                
                </span>
                <div className="flex items-center justify-center space-x-1 rounded-md cursor-pointer hover:bg-gray-400/18 px-2 py-1">
                  <Eye className="w-4 h-4" />
                  <span className="text-xs">Show Formated JSON</span>
                </div>
              </div>)}
              
              <div className="w-full "></div>
            </div>
          </>
        )}
      </div>
    </>
  );
};
